// A name box backed by two tiers of places: type a couple of characters and
// stored places drop down beneath it, keep going and geocoder candidates
// appear under those. Pick one and the caller gets its position, or just keep
// typing -- suggestions are additive and never block or rewrite the text.
// Built as a plain input + styled list (the ARIA combobox pattern) rather
// than react-bootstrap's Dropdown, whose toggle-button focus model is wrong
// for typing-while-open.
//
// The stored tier answers from memory and is always ranked first; the remote
// tier is debounced, starts a character later, and only ever APPENDS. That
// append-only rule is what makes the keyboard safe: activeIndex is an index
// into the rendered list, and remote results routinely land while the user is
// already arrowing through it. Because no local row can change position when
// remote arrives, an arrowed-to row cannot change underfoot between ArrowDown
// and Enter -- so only the local arm resets activeIndex, and the remote arm
// must never touch it. For the same reason a remote batch is held back until
// the local batch for the SAME query has landed (mergeSuggestions owns the
// rest of the rule, including the caps that make the append safe to trim).
//
// Options handle onMouseDown (with preventDefault) instead of onClick so the
// input never blurs before the pick lands -- blur is how the trip page
// commits a rename, and a pick must not race it.
//
// A geocoder row shows its city/state/country to tell the many Springfields
// apart, but a pick commits only the bare name: that string goes straight
// into the waypoint and becomes its nameKey, and "Cap Sante, Skagit County,
// Washington, United States" is a name no second user would ever type, so the
// shared database would never match itself.

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import Form from 'react-bootstrap/Form';
import { formatLatLon } from '../fleet-shared/TripRoute.mjs';
import { mergeSuggestions, suggestPlaces, suggestRemotePlaces } from '../utilities/places.mjs';

// Long enough that a steady typist doesn't fire per character, short enough
// that the list doesn't feel broken. Trailing edge only.
const SUGGEST_DEBOUNCE_MS = 350;

const NO_SUGGESTIONS = { local: null, localQuery: '', remote: null, remoteQuery: null };

const PlaceSuggestInput = ({
  id,
  size,
  value,
  placeholder,
  ariaLabel,
  selectOnFocus,
  onChange,
  onPick,
  onFocus,
  onBlur,
}) => {
  const listboxId = useId();
  const [tiers, setTiers] = useState(NO_SUGGESTIONS);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  // The text as of the latest keystroke (or pick); async suggestion results
  // for anything older are dropped instead of reopening a stale list.
  const queryRef = useRef(value || '');

  // Whether the box still has focus. The last keystroke's lookup can land
  // after the user has tabbed or clicked away -- by then blur has closed the
  // list and the trip page has committed the rename, so an answer for text
  // that is no longer being edited must not pop the dropdown back open over
  // a settled row. The text guard above can't catch this one: the query is
  // current, it's the editing session that ended.
  const focusedRef = useRef(false);

  // The pending debounce and the request it will start, so both can be called
  // off on the next keystroke, on blur, on Escape, and on unmount.
  const timerRef = useRef(null);
  const abortRef = useRef(null);

  // A remote batch only counts once local has answered the same query --
  // otherwise a fast Photon reply could render first and then get local rows
  // PREPENDED under the cursor, which is the one thing the append-only rule
  // exists to prevent.
  const suggestions = useMemo(() => {
    if (!tiers.local) return [];
    const remote = tiers.remoteQuery === tiers.localQuery ? tiers.remote : null;
    return mergeSuggestions(tiers.local, remote);
  }, [tiers]);

  // `open` only records whether the list has been dismissed; whether there is
  // anything to show is a separate question, so a remote batch can still open
  // a list that local left empty.
  const expanded = open && suggestions.length > 0;

  function cancelRemote() {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  }

  function close() {
    cancelRemote();
    setOpen(false);
    setActiveIndex(-1);
  }

  // Refs only, so the cleanup needs no dependencies and can't go stale.
  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (abortRef.current) abortRef.current.abort();
    },
    []
  );

  function handleChange(text) {
    onChange(text);
    queryRef.current = text;
    setOpen(true);
    cancelRemote();

    // The stored tier, immediately. The previous list stays rendered until
    // this answers, so the dropdown doesn't blink between keystrokes.
    // suggestPlaces promises never to reject, but the memo's `!tiers.local`
    // gate would hold back BOTH tiers forever if it ever did -- so a failure
    // becomes an empty local batch and the geocoder tier still renders.
    suggestPlaces(text).catch(() => []).then((places) => {
      if (queryRef.current !== text || !focusedRef.current) return;
      setTiers((current) => ({ ...current, local: places, localQuery: text }));
      // A new local batch is a genuinely new list -- nothing keeps its index.
      setActiveIndex(-1);
    });

    // The geocoder, once the typing pauses.
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      if (queryRef.current !== text || !focusedRef.current) return;

      const controller = new AbortController();
      abortRef.current = controller;
      suggestRemotePlaces(text, { signal: controller.signal }).then((places) => {
        if (abortRef.current === controller) abortRef.current = null;
        if (queryRef.current !== text || !focusedRef.current) return;
        setTiers((current) => ({ ...current, remote: places, remoteQuery: text }));
      });
    }, SUGGEST_DEBOUNCE_MS);
  }

  function pick(place) {
    queryRef.current = place.name;
    close();
    setTiers(NO_SUGGESTIONS);
    // `source` is 'photon' only for a geocoder candidate -- stored rows carry
    // the database's own 'geocode'/'user' -- so it is how the trip page knows
    // a pick brought in a place the shared database has never seen.
    //
    // `id` is the gazetteer row's, so the trip page can BIND the waypoint to
    // it rather than copy its coordinates and lose the link. A Photon
    // candidate's id is OSM's, not ours, so it is dropped: `source` already
    // marks that tier, and passing a foreign id would bind the waypoint to a
    // row that doesn't exist.
    onPick({
      name: place.name,
      lat: place.lat,
      lon: place.lon,
      source: place.source,
      id: place.source === 'photon' ? null : place.id,
    });
  }

  function handleKeyDown(event) {
    if (!expanded) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % suggestions.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => (index <= 0 ? suggestions.length - 1 : index - 1));
    } else if (event.key === 'Enter') {
      // Only an arrowed-to item hijacks Enter; otherwise the key stays native
      // so the box behaves like the plain input it replaced.
      if (activeIndex >= 0) {
        event.preventDefault();
        pick(suggestions[activeIndex]);
      }
    } else if (event.key === 'Escape') {
      // While the list is open, Escape means "close the list", not whatever
      // an enclosing modal would do with it.
      event.stopPropagation();
      close();
    } else if (event.key === 'Tab') {
      close();
    }
  }

  return (
    <div className="position-relative">
      <Form.Control
        id={id}
        size={size}
        type="text"
        role="combobox"
        value={value}
        aria-label={ariaLabel}
        aria-expanded={expanded}
        aria-autocomplete="list"
        aria-controls={listboxId}
        aria-activedescendant={activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined}
        placeholder={placeholder}
        onChange={(event) => handleChange(event.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={(event) => {
          focusedRef.current = true;
          // An auto-generated name is a placeholder the user is expected to
          // replace, not edit -- select it on focus so typing overwrites it
          // in one gesture instead of appending to the coordinate string.
          if (selectOnFocus) event.target.select();
          if (onFocus) onFocus();
        }}
        onBlur={(event) => {
          focusedRef.current = false;
          close();
          if (onBlur) onBlur(event);
        }}
      />
      {expanded && (
        <ul
          className="dropdown-menu show w-100"
          style={{ top: '100%', left: 0 }}
          role="listbox"
          id={listboxId}
          data-testid="place-suggestions"
        >
          {suggestions.map((place, index) => (
            <li
              key={place.id}
              id={`${listboxId}-option-${index}`}
              role="option"
              aria-selected={index === activeIndex}
              className={`dropdown-item small${index === activeIndex ? ' active' : ''}`}
              style={{ cursor: 'pointer' }}
              onMouseEnter={() => setActiveIndex(index)}
              onMouseDown={(event) => {
                event.preventDefault();
                pick(place);
              }}
            >
              {place.name}{' '}
              {place.source !== 'photon' && (
                <>
                  {/* Provenance: a stored gazetteer row vs a fresh geocoder
                      hit. Deliberately part of the option's accessible name
                      (the NearbyPlacesModal ", curated" precedent). */}
                  <span
                    className={`badge ${place.curated ? 'text-bg-primary' : 'text-bg-secondary'} align-middle`}
                  >
                    {place.curated ? 'Curated' : 'Saved'}
                  </span>{' '}
                </>
              )}
              <span className={index === activeIndex ? undefined : 'text-body-secondary'}>
                {place.context ? `${place.context} · ` : ''}
                {formatLatLon(place.lat, place.lon)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PlaceSuggestInput;
